import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Genera el PDF de un documento a partir de su vista previa.
 *
 * La versión anterior metía toda la vista en una sola página: cuando el
 * documento no entraba en A4, lo encogía hasta que cupiera. Una factura de doce
 * líneas salía al 66% del ancho de la hoja y una de treinta, al 36%: texto
 * ilegible y márgenes blancos enormes a los lados.
 *
 * Ahora el documento se reparte en tantas páginas A4 como haga falta, siempre al
 * ancho completo de la hoja, y los cortes se hacen entre filas de la tabla para
 * no partir un ítem por la mitad.
 */

// A4 en milímetros
const A4_ANCHO = 210;
const A4_ALTO = 297;
const MARGEN = 8;

/** Posiciones donde se puede cortar sin partir una fila, en píxeles de canvas. */
const calcularCortesSeguros = (elemento, escala) => {
  const origen = elemento.getBoundingClientRect().top;
  const filas = elemento.querySelectorAll('tr, [role="row"]');
  const cortes = [];
  filas.forEach((fila) => {
    const caja = fila.getBoundingClientRect();
    if (caja.height > 0) cortes.push(Math.round((caja.bottom - origen) * escala));
  });
  return [...new Set(cortes)].sort((a, b) => a - b);
};

/**
 * Dónde termina esta página: el último corte seguro que entre en el alto
 * disponible. Si no hay ninguno (por ejemplo un bloque más alto que la página),
 * se corta por el alto máximo para no entrar en un bucle infinito.
 */
const buscarCorte = (desde, maximo, cortes, altoTotal) => {
  const limite = Math.min(desde + maximo, altoTotal);
  if (limite >= altoTotal) return altoTotal;

  const candidatos = cortes.filter((c) => c > desde && c <= limite);
  if (candidatos.length) return candidatos[candidatos.length - 1];
  return limite;
};

export const generatePDF = async (invoice, options = {}) => {
  const {
    fileName = `factura_${invoice.number || 'nueva'}.pdf`,
  } = options;

  try {
    let element = document.getElementById('invoice-preview')
      || document.querySelector('.MuiDialogContent-root')
      || document.querySelector('.MuiPaper-root');

    if (!element) {
      throw new Error('No se pudo encontrar el elemento de vista previa de factura');
    }

    const paperElement = element.querySelector('.MuiPaper-root');
    const targetElement = paperElement || element;

    // El selector de estilos es parte de la aplicación, no del documento.
    const styleSelector = document.querySelector('.style-selector');
    if (styleSelector) styleSelector.style.display = 'none';

    const escala = 2; // Para que el texto no salga pixelado
    let canvas;
    try {
      canvas = await html2canvas(targetElement, {
        scale: escala,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        height: targetElement.scrollHeight,
        windowHeight: targetElement.scrollHeight
      });
    } finally {
      if (styleSelector) styleSelector.style.display = '';
    }

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const anchoUtil = A4_ANCHO - MARGEN * 2;
    const altoUtil = A4_ALTO - MARGEN * 2;
    // Cuántos píxeles del canvas entran en el alto útil, manteniendo la escala
    // que impone el ancho: el documento siempre ocupa el ancho completo.
    const pxPorMm = canvas.width / anchoUtil;
    const altoPaginaPx = Math.floor(altoUtil * pxPorMm);

    const cortes = calcularCortesSeguros(targetElement, escala);

    let desde = 0;
    let pagina = 0;
    const paginas = [];

    while (desde < canvas.height) {
      const hasta = buscarCorte(desde, altoPaginaPx, cortes, canvas.height);
      paginas.push([desde, hasta]);
      desde = hasta;
      if (paginas.length > 50) break; // guarda contra un documento anómalo
    }

    paginas.forEach(([inicio, fin], indice) => {
      const alto = fin - inicio;
      // Cada trozo se recorta a un canvas propio antes de ir a la página.
      const recorte = document.createElement('canvas');
      recorte.width = canvas.width;
      recorte.height = alto;
      const ctx = recorte.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, recorte.width, recorte.height);
      ctx.drawImage(canvas, 0, inicio, canvas.width, alto, 0, 0, canvas.width, alto);

      if (indice > 0) pdf.addPage();
      pdf.addImage(recorte.toDataURL('image/png'), 'PNG', MARGEN, MARGEN, anchoUtil, alto / pxPorMm);
      pagina = indice + 1;
    });

    // Pie de página, sólo si hay más de una: en un documento de una hoja sobra.
    if (paginas.length > 1) {
      for (let i = 1; i <= paginas.length; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(120);
        pdf.text(`Página ${i} de ${paginas.length}`, A4_ANCHO - MARGEN, A4_ALTO - 3, { align: 'right' });
      }
    }

    pdf.save(fileName);
    console.log(`PDF generado: ${fileName} (${paginas.length} página${paginas.length > 1 ? 's' : ''})`);
    return { success: true, fileName, paginas: paginas.length };

  } catch (error) {
    console.error('Error generando PDF:', error);
    return { success: false, error: error.message };
  }
};

export default generatePDF;
